import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch} from 'react-redux';
import {Formik} from 'formik';
import {isEqual} from 'lodash';
import moment from 'moment';
import {useUnpaidAgentsUIContext} from '../context/UnpaidAgentsUIContext';
import {FilterFromDate, FilterToDate} from '../../_common/components/FilterDateRange';
import FilterTextField from '../../_common/components/FilterTextField';
import ButtonExportToExcel from '../../_common/components/ButtonExportToExcel';
import FiltersBar from '../../_common/components/FiltersBar';
import {RequestCancellationManager} from '../../_common/utils/RequestCancellationManager';
import * as actions from '../_redux/unpaidAgentsActions';
import {getUnpaidAgentsListIDPrefix} from '../_redux/unpaidAgentsActions';
import {generateExcelFile} from '../../_common/utils/excel';
import {unpaidAgentsHeader} from '../../_common/constants/exportedFilesMainHeaders';
import {headersUnpaidAgents} from '../../_common/constants/exportedFilesHeaders';

const prepareFilter = (queryParams, values) => {
  const {name, agentID, idNumber, tamalID, fromDate, toDate} = values;
  const newQueryParams = {...queryParams};
  const filter = {};
  if (Boolean(name)) {
    filter.name = name;
  } else {
    delete newQueryParams.name;
  }
  if (Boolean(agentID)) {
    filter.agentID = agentID;
  } else {
    delete newQueryParams.agentID;
  }
  if (Boolean(idNumber)) {
    filter.idNumber = idNumber;
  } else {
    delete newQueryParams.idNumber;
  }
  if (Boolean(tamalID)) {
    filter.tamalID = tamalID;
  } else {
    delete newQueryParams.tamalID;
  }
  if (Boolean(fromDate)) {
    filter.fromDate = moment(fromDate).format('YYYY-MM-DD');
  } else {
    delete newQueryParams.fromDate;
  }
  if (Boolean(toDate)) {
    filter.toDate = moment(toDate).format('YYYY-MM-DD');
  } else {
    delete newQueryParams.toDate;
  }

  return {...newQueryParams, ...filter};
};

export function UnpaidAgentsFilter() {
  const dispatch = useDispatch();
  const {formatMessage} = useIntl();
  const context = useUnpaidAgentsUIContext();
  const props = useMemo(() => {
    return {
      queryParams: context.queryParams,
      setQueryParams: context.setQueryParams,
    };
  }, [context]);
  const [loading, setLoading] = useState(false);

  const applyFilter = (values) => {
    RequestCancellationManager.stopRequests(getUnpaidAgentsListIDPrefix);
    const newQueryParams = prepareFilter(props.queryParams, values);
    if (!isEqual(newQueryParams, props.queryParams)) {
      props.setQueryParams(newQueryParams);
    }
  };

  const qs = new URLSearchParams(window.location.search);
  const name = qs.get('name') || '';
  const agentID = qs.get('agentID') || '';
  const idNumber = qs.get('idNumber') || '';
  const tamalID = qs.get('tamalID') || '';
  const today = moment();
  const fromDate = qs.get('fromDate') || today.subtract(1, 'days').format('YYYY-MM-DD');
  const toDate = qs.get('toDate') || today.add(1, 'days').format('YYYY-MM-DD');

  const newQueryParams = {...props.queryParams, fromDate, toDate};
  if (!isEqual(newQueryParams, props.queryParams)) {
    props.setQueryParams(newQueryParams);
  }

  const exportedFromDate = fromDate ? ['מתאריך -', moment(fromDate).format('DD-MM-YYYY')].join(' ') : '';
  const exportedToDate = toDate ? ['עד תאריך -', moment(toDate).format('DD-MM-YYYY')].join(' ') : '';
  const datesRange = [exportedFromDate, exportedToDate].filter(Boolean).join(' / ');

  const exportUnpaidAgentsFileFunc = () => {
    setLoading(true);
    dispatch(actions.exportUnpaidAgentsFileAction(props.queryParams))
      .then(prepareDataForExcel)
      .then((data) => {
        generateExcelFile(
          data,
          'Unpaid agents.xlsx',
          [unpaidAgentsHeader, datesRange].filter(Boolean).join(' - ')
        );
      })
      .catch((error) => {
        console.error(`Error: + ${error}`);
        setLoading(false);
      })
      .finally(() => setLoading(false));
  };
  const prepareDataForExcel = data => {
    return data.map((item, index) => {
      const {
        hasLawAdvancePayment,
        firstName,
        lastName,
        agentID,
        tamalID,
        phone,
        form101FileExists,
        taxAdjustmentFileExists,
        isOtherIncomeExists
      } = item;

      const otherIncome = isOtherIncomeExists
        ? taxAdjustmentFileExists ? 'קיים' : 'לא קיים'
        : 'לא נדרש';

      return {
        [headersUnpaidAgents[0]]: index + 1,
        [headersUnpaidAgents[1]]: hasLawAdvancePayment ? 'כן' : 'לא',
        [headersUnpaidAgents[2]]: firstName,
        [headersUnpaidAgents[3]]: lastName,
        [headersUnpaidAgents[4]]: agentID,
        [headersUnpaidAgents[5]]: tamalID,
        [headersUnpaidAgents[6]]: phone,
        [headersUnpaidAgents[7]]: form101FileExists ? 'קיים' : 'לא קיים',
        [headersUnpaidAgents[8]]: otherIncome,
      };
    });
  };

  const containerClassnames = 'w-200px position-relative my-1 px-3';

  return (
    <Formik
      initialValues={{
        name,
        agentID,
        idNumber,
        tamalID,
        fromDate,
        toDate
      }}
      onSubmit={(values) => applyFilter(values)}
      enableReinitialize={true}
    >
      {({values, ...formikProps}) => (
        <FiltersBar
          filters={
            <div className={'d-flex flex-wrap justify-content-center align-items-center mb-0 w-100'}>
              <FilterFromDate
                values={values}
                {...formikProps}
                customClearDate={() => {
                  formikProps.setFieldValue('fromDate', today.subtract(1, 'days').toDate());
                }}
                containerStyles={containerClassnames}
              />
              <FilterToDate
                values={values}
                {...formikProps}
                customClearDate={() => {
                  formikProps.setFieldValue('toDate', today.add(1, 'days').toDate());
                }}
                containerStyles={containerClassnames}
              />
              <FilterTextField
                fieldName="name"
                placeholder={formatMessage({id: 'unpaidAgents-filter-name', defaultMessage: 'Name'})}
                value={values.name}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="agentID"
                placeholder={formatMessage({id: 'unpaidAgents-filter-agentID', defaultMessage: 'Agent Id'})}
                value={values.agentID}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="tamalID"
                placeholder={formatMessage({id: 'unpaidAgents-filter-tamalID', defaultMessage: 'Tamal Id'})}
                value={values.tamalID}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="idNumber"
                placeholder={formatMessage({id: 'unpaidAgents-filter-idNumber', defaultMessage: 'User Id'})}
                value={values.idNumber}
                containerStyles={containerClassnames}
                {...formikProps}
              />
            </div>
          }
          buttons={
            <ButtonExportToExcel loading={loading} exportFunc={exportUnpaidAgentsFileFunc}/>
          }
        />
      )}
    </Formik>
  );
}